import { useState, useCallback, useRef } from 'react'
import ConfirmModalHook from '@/components/common/ConfirmModal/ConfirmModal.hook'
import message from '@/libs/message'
import { useCreateClaimReject } from '@/services/healthClaim/useCreateClaimReject'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import CONSTANT from '@/constant'
import { useRejectClaim } from '@/services/healthClaim/useRejectClaim'
import { useRecoilState, useSetRecoilState } from 'recoil'
import rejectClaimModalAtom from '@/recoil/rejectClaimModal'
import previewPDFModalAtom from '@/recoil/previewPDFModal'
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import confirmModalAtom from '@/recoil/confirmModal'

function useRejectClaimModal() {
  const params = useParams()
  const { claimFormId } = params

  const { data, isPending } = useRejectClaim(claimFormId || '')

  const navigate = useNavigate()
  const [renderPDF, setRenderPDF] = useState<string>(data?.html || '')
  const [renderFullPDF, setRenderFullPDF] = useState<string>('')
  const [rejectReason, setRejectReason] = useState<string>(data?.claimRejectDetail || '')
  const [zoomLevel, setZoomLevel] = useState<number>(0.65)

  const [openRejectClaimModal, setRejectClaimModal] = useRecoilState(rejectClaimModalAtom)
  const [openPreviewPDFModal, setPreviewPDFModal] = useRecoilState(previewPDFModalAtom)

  const contentRef = useRef<HTMLDivElement>(null)
  const contentRefPreview = useRef<HTMLDivElement>(null)

  const setConfirmModal = useSetRecoilState(confirmModalAtom)

  const queryClient = useQueryClient()

  const closeModal = useCallback(() => {
    setRejectClaimModal(false)
  }, [setRejectClaimModal])

  const { mutateAsync: rejectClaim, isPending: isRejectClaimPending } = useCreateClaimReject()

  const handleCancel = () => {
    setRejectReason('')
    closeModal()
    if (contentRef.current) {
      contentRef.current.innerHTML = data?.html || ''
    }
    if (contentRefPreview.current) {
      contentRefPreview.current.innerHTML = data?.html || ''
    }
  }

  const handleSubmit = useCallback(() => {
    if (rejectReason.trim()) {
      if (claimFormId)
        setConfirmModal({
          isVisible: true,
          title: 'ยืนยันปฏิเสธการจ่ายค่าสินไหมทดแทน',
          bodyText: 'คุณได้ตรวจสอบข้อมูลว่าถูกต้องครบถ้วนแล้ว',
          okText: 'ยืนยัน',
          cancelText: 'ยกเลิก',
          dataTestId: 'reject-claim-confirmation-modal',
          okButtonProps: { loading: isRejectClaimPending },
          onOkFunction: async () => {
            try {
              // Show loading on confirm button (in modal)
              setConfirmModal((prev) => ({
                ...prev,
                okButtonProps: { ...prev.okButtonProps, loading: true },
              }))

              // Call API to reject claim
              await rejectClaim({ claimFormId: claimFormId, claimRejectDetail: rejectReason }).then(
                () => {
                  setRejectReason('')
                  if (contentRef.current) {
                    contentRef.current.innerHTML = data?.html || ''
                  }

                  // Ensure detail is fresh when navigating
                  queryClient.refetchQueries({
                    queryKey: ['useGetClaimDetail', claimFormId],
                  })

                  message.success({ content: 'ปฏิเสธสำเร็จ', duration: 10 })

                  return navigate(generatePath(CONSTANT.ROUTES.HEALTH_CLAIM_PAGE))
                },
              )
            } catch (error: any) {
              setRejectReason('')
              if (contentRef.current) {
                contentRef.current.innerHTML = data?.html || ''
              }
              message.error({
                content: 'ปฏิเสธไม่สำเร็จ',
                duration: 10,
              })
            } finally {
              setConfirmModal({
                isVisible: false,
                onOkFunction: Function,
              })
              setRejectClaimModal(false)
            }
          },
        })
    }
  }, [
    rejectReason,
    claimFormId,
    setConfirmModal,
    rejectClaim,
    navigate,
    data?.html,
    queryClient,
    setRejectClaimModal,
    isRejectClaimPending,
  ])

  const items: Array<{
    key: string
    label: string
    onClick: () => void
  }> = [
    {
      key: '75',
      label: '75%',
      onClick: () => setZoomLevel(0.4875),
    },
    {
      key: '100',
      label: '100%',
      onClick: () => setZoomLevel(0.65),
    },
    {
      key: '125',
      label: '125%',
      onClick: () => setZoomLevel(0.8125),
    },
    {
      key: '150',
      label: '150%',
      onClick: () => setZoomLevel(0.975),
    },
  ]

  const getLabelZoom = (level: number) => {
    if (level === 0.4875) return '75%'
    if (level === 0.65) return '100%'
    if (level === 0.8125) return '125%'
    if (level === 0.975) return '150%'
    return `${Math.round((level / 0.65) * 100)}`
  }

  const isContentOverflowing = () => {
    if (contentRef.current) {
      return contentRef.current.scrollHeight > contentRef.current.clientHeight
    }
    return false
  }

  const onChangeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (isContentOverflowing() && e.target.value.length > rejectReason.length) return

    setRejectReason(e.target.value)

    const parser = new DOMParser()
    const doc = parser.parseFromString(data?.html || '', 'text/html')
    const detailDiv = doc.getElementById('claim-reject-detail')
    if (detailDiv) {
      /* REMINDER:
          วิธีการแปลงข้อความที่กรอกใน TextArea ให้เป็น HTML ที่มีแท็ก <p> สำหรับแต่ละบรรทัด:
          1. แทนที่ <br>, <br/>, </br> ด้วย \n
             ใช้ .replace(/(<br\s*\/?>|<\/br>)/gi, '\n') เพื่อให้ขึ้นบรรทัดใหม่ในข้อความกลายเป็นบรรทัดใหม่จริง ๆ
  
          2. แยกข้อความเป็นแต่ละบรรทัด
             ใช้ .split('\n') เพื่อแยกข้อความเป็น array ตามบรรทัด
  
          3. กรองบรรทัดว่าง
             ใช้ .filter((line) => line.trim() !== '') เพื่อเอาบรรทัดที่ว่างออก
  
          4. ครอบแต่ละบรรทัดด้วย <p>
             ใช้ .map((line) => `<p>${line}</p>`) เพื่อให้แต่ละบรรทัดอยู่ในแท็ก <p>
  
          5. รวมกลับเป็น string เดียว
             ใช้ .join('') เพื่อรวม array กลับเป็น string HTML
        */
      const valueWithParagraphs = e.target.value
        .replace(/(<br\s*\/?>|<\/br>)/gi, '\n') // แทนที่ <br>, <br/>, </br> ด้วย \n
        .split('\n')
        .filter((line) => line.trim() !== '')
        .map((line) => `<p>${line}</p>`)
        .join('')

      detailDiv.innerHTML = valueWithParagraphs
      setRenderPDF(doc.documentElement.innerHTML)
    }
  }

  useEffect(() => {
    if (!isPending) {
      setRenderPDF(data?.html || '')
      setRejectReason(data?.claimRejectDetail || '')
    }
  }, [data, isPending])

  return {
    openRejectClaimModal,
    handleCancel,
    handleSubmit,
    rejectReason,
    zoomLevel,
    renderPDF,
    contentRef,
    items,
    getLabelZoom,
    openPreviewPDFModal,
    setPreviewPDFModal,
    onChangeText,
    data,
    renderFullPDF,
    setRenderFullPDF,
    contentRefPreview,
    isPending,
  }
}

export default useRejectClaimModal
